import React, { useState, useRef, useEffect } from "react";
import { Button } from "reactstrap";

/**
 * ScanButton component to handle VIN scanning using the camera.
 *
 * @param {Object} props - Component props
 * @param {string} props.vin - The VIN to be scanned
 * @param {Function} props.handleTakePhoto - Function to handle the photo taken event
 * @param {Function} props.setShowModal - Function to toggle the modal visibility
 * @returns {JSX.Element} The rendered component
 */
const ScanButton = ({ vin, handleTakePhoto, setShowModal }) => {
    const [isCameraVisible, setCameraVisible] = useState(false);
    const [stream, setStream] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (isCameraVisible) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera();
    }, [isCameraVisible]);

    const startCamera = async () => {
        try {
            console.log("Starting native camera...");
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                console.log("Camera stream connected to video element");
            }
        } catch (error) {
            console.error("Error accessing camera:", error);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
            console.log("Camera stopped");
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Set canvas size to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to data URI
        const dataUri = canvas.toDataURL('image/jpeg', 0.8);

        console.log("Photo captured! Data URI length:", dataUri.length);

        // Process the photo
        setShowModal(true);
        handleTakePhoto(dataUri);
        setCameraVisible(false);
    };

    return (
        <div className="noDesktop">
            {isCameraVisible ? (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    backgroundColor: "rgba(0,0,0,0.9)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    <Button
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            zIndex: 10000,
                            backgroundColor: "white",
                            color: "black"
                        }}
                        className="btn-sm"
                        onClick={() => setCameraVisible(false)}
                    >
                        ✕ Close
                    </Button>

                    <div style={{
                        width: "90%",
                        maxWidth: "400px",
                        height: "200px",
                        overflow: "hidden",
                        borderRadius: "8px",
                        border: "2px solid white",
                        backgroundColor: "black"
                    }}>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                            }}
                        />
                    </div>

                    <Button
                        style={{
                            marginTop: "30px",
                            backgroundColor: "#28a745",
                            border: "3px solid white",
                            padding: "15px 30px",
                            borderRadius: "50px",
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "white",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.5)"
                        }}
                        onClick={capturePhoto}
                    >
                        📷 TAKE PHOTO
                    </Button>

                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
            ) : (
                <Button
                    className="btn-md btn btn-primary ml-2"
                    onClick={() => setCameraVisible(true)}
                >
                    Scan VIN
                </Button>
            )}
            {vin && <p>Detected VIN: {vin}</p>}
        </div>
    );
};

export default ScanButton;
